import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  CalendarContainer,
  CalendarTable,
  TableHeaderCell,
  TrainerHeaderLabel,
  TimeCell,
  EventCell,
  RowDivider,
  CurrentTimeIndicator,
  CurrentTimeDot,
  DragGhostCard,
  DragTimeLabel,
  TrainerHeaderRow,
  TrainerArrowButton,
  TrainerHeaderCounts,
  TrainerHeaderCountItem,
  ATTEND_STATUS_META,
  SUMMARY_EXTRA_COUNT_META,
  FillerRegisterButton,
} from './PrivateLessonStyled';
import { renderEventCards } from './PrivateLessonEventCard';
import { summarizeEvents, TYPE_FILTER_ALL, TYPE_FILTER_PRIVATE, TYPE_FILTER_OT } from './lessonStatus';

// 드래그 인식까지 필요한 최소 이동 거리(px). 이보다 작은 움직임은 클릭으로 간주.
const DRAG_THRESHOLD_PX = 5;
const MINUTES_PER_HOUR = 60;
const DAY_END_MINUTES = 24 * MINUTES_PER_HOUR;
// 강사 컬럼 드래그 — 커서를 따라다니는 컬럼 미리보기 높이.
// 고정값이면 열이 짧게 잘려 보여서, 화면에 보이는 컬럼과 같은 길이감이 나도록 캘린더 가시 영역에 맞춘다.
const COLUMN_PREVIEW_HEIGHT_RATIO = 0.9;
const COLUMN_PREVIEW_MIN_HEIGHT_PX = 360;
// 강사 컬럼 드래그 — 좌우 끝 이 범위 안으로 들어오면 자동 가로 스크롤
const EDGE_SCROLL_ZONE_PX = 90;
const EDGE_SCROLL_MAX_SPEED_PX = 22;

/**
 * 드래그 중 커서를 따라다닐 "세로 컬럼" 이미지를 만든다.
 * 표의 열은 DOM 상 하나의 요소가 아니라 행마다 흩어져 있어서, 헤더 셀과 같은 열의 셀들을
 * 복제해 세로로 쌓아 하나의 요소로 만든 뒤 setDragImage 에 넘긴다.
 * 반환한 요소는 호출 측에서 제거해야 한다.
 */
const buildColumnDragImage = (headerCell, maxHeightPx) => {
  const table = headerCell.closest('table');
  const columnIndex = Array.from(headerCell.parentElement.children).indexOf(headerCell);
  if (!table || columnIndex < 0) return null;

  const preview = document.createElement('div');
  preview.style.cssText = [
    'position:fixed',
    'top:-10000px',
    'left:0',
    `width:${headerCell.offsetWidth}px`,
    'background:#fff',
    'border:2px solid #4AB3BC',
    'border-radius:8px',
    'box-shadow:0 14px 28px rgba(0,0,0,0.3)',
    'overflow:hidden',
    'pointer-events:none',
  ].join(';');

  const columnCells = [headerCell, ...Array.from(table.tBodies[0]?.rows ?? [], row => row.children[columnIndex])];
  let stackedHeight = 0;
  columnCells.forEach(cell => {
    if (!cell || stackedHeight >= maxHeightPx) return;
    const clone = cell.cloneNode(true);
    // th/td 를 표 밖으로 꺼내는 것이라 블록으로 바꾸고 sticky 를 푼다.
    clone.style.display = 'block';
    clone.style.position = 'static';
    clone.style.width = '100%';
    clone.style.height = `${cell.offsetHeight}px`;
    clone.style.boxSizing = 'border-box';
    preview.appendChild(clone);
    stackedHeight += cell.offsetHeight;
  });

  document.body.appendChild(preview);
  return preview;
};


const PrivateLessonCalendar = ({
  hours, // 행 목록 — { label, startMinutes(자정부터 분), minutes(행 길이) }. 마지막 행은 표 칸보다 짧을 수 있다
  trainers,
  filteredTrainers,
  periodTimes,
  events,
  currentDate,
  cellHeight = 80, // 1시간 픽셀 (줌 상태). 행 높이는 행 길이(분)에 비례한다 — 칸 단위와 무관하게 시간→px 는 이 값 기준
  timeUnitMinutes = 60, // 표 칸 단위(분) — 지점 스케줄 설정(30/60/120)
  snapMinutes = 60, // 선택 단위(분) — 빈칸 hover·클릭·우클릭·카드 드래그 스냅 간격. 표 칸과 독립
  cardMinMinutes = 48, // 짧은 일정 카드의 최소 높이(분). 기본 48분 = 1시간의 80%
  visibleCounts, // 지점 스케줄 설정의 표시 항목. 끈 상태 항목(예약·출석·결석)은 강사별 숫자에서도 숨긴다
  onCellClick,
  onCellContextMenu,
  onEventClick,
  onEventContextMenu,
  onDragTimeChange,
  onTrainerMove,
  onTrainerReorder,
  canDragEvent = true,
  typeFilter,
  ticketAlertCount,
  ticketAlertCountUnit,
}) => {
  const divRef = useRef(null);
  const navigate = useNavigate();
  const cardMinHeightPx = (cardMinMinutes / MINUTES_PER_HOUR) * cellHeight;
  // 표 마지막 시각(자정부터 분) — 선택 구간이 이 뒤로 넘지 않게 자른다
  const lastRow = hours[hours.length - 1];
  const rangeEndMinutes = lastRow ? lastRow.startMinutes + lastRow.minutes : DAY_END_MINUTES;

  // 셀 안 마우스 위치 → 그 위치가 속한 선택 단위 구간의 시작 시각(분). 표 칸 경계와 무관하다.
  // 셀 테두리 픽셀에서 옆 행으로 넘어가지 않도록 행 안(0 ~ 행 길이-1분)으로 자른다.
  const snapStartAt = (e, row) => {
    const offsetY = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const within = Math.min(Math.max(Math.floor((offsetY / cellHeight) * MINUTES_PER_HOUR), 0), row.minutes - 1);
    return Math.floor((row.startMinutes + within) / snapMinutes) * snapMinutes;
  };

  // 빈칸 hover 박스를 선택 단위 구간에 맞춘다 — 리렌더 없이 셀 CSS 변수만 바꾼다(EventCell ::after).
  // 구간이 행과 정확히 같으면 변수를 지워 기존 모양(셀 전체) 그대로 둔다.
  const updateHoverBox = (e, row) => {
    const cell = e.currentTarget;
    const start = snapStartAt(e, row);
    const length = Math.min(snapMinutes, rangeEndMinutes - start);
    if (start === row.startMinutes && length === row.minutes) {
      cell.style.removeProperty('--hover-top');
      cell.style.removeProperty('--hover-height');
      return;
    }
    cell.style.setProperty('--hover-top', `${((start - row.startMinutes) / MINUTES_PER_HOUR) * cellHeight}px`);
    cell.style.setProperty('--hover-height', `${(length / MINUTES_PER_HOUR) * cellHeight}px`);
  };
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const hasDraggedRef = useRef(false);

  // 이벤트 카드 드래그 상태
  const [dragEvent, setDragEvent] = useState(null);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragGhostPos, setDragGhostPos] = useState(null);
  const isDraggingEventRef = useRef(false);
  const dragThresholdRef = useRef(false);
  // 드래그로 끌었다 놓은 직후 따라오는 click 한 번을 무시하기 위한 표식(상세 팝오버·생성 모달 오픈 방지)
  const suppressClickRef = useRef(false);

  // 강사 컬럼 순서 드래그 상태
  const [dragTrainerId, setDragTrainerId] = useState(null);
  const [dropTargetTrainerId, setDropTargetTrainerId] = useState(null);
  const dragPointerXRef = useRef(0);
  const edgeScrollFrameRef = useRef(null);

  // 가장자리 자동 가로 스크롤 — 강사 컬럼 순서 드래그와 카드 드래그가 함께 쓴다
  const stopEdgeAutoScroll = useCallback(() => {
    if (edgeScrollFrameRef.current) {
      cancelAnimationFrame(edgeScrollFrameRef.current);
      edgeScrollFrameRef.current = null;
    }
  }, []);

  // 드래그 중 커서가 좌우 끝에 닿으면 가로 스크롤을 이어서 굴린다.
  // 네이티브 드래그 중에는 스크롤 이벤트를 만들 방법이 없어 rAF 로 직접 굴린다.
  // findTrainerIdAtX 는 아래에서 정의되므로 rAF 루프는 ref 로 최신 함수를 부른다
  const findTrainerIdAtXRef = useRef(null);
  const startEdgeAutoScroll = useCallback(() => {
    if (edgeScrollFrameRef.current) return;
    const step = () => {
      const container = divRef.current;
      if (container) {
        const { left, right } = container.getBoundingClientRect();
        const pointerX = dragPointerXRef.current;
        const leftOverflow = EDGE_SCROLL_ZONE_PX - (pointerX - left);
        const rightOverflow = EDGE_SCROLL_ZONE_PX - (right - pointerX);
        const scrolledFrom = container.scrollLeft;
        if (leftOverflow > 0) {
          container.scrollLeft -= (leftOverflow / EDGE_SCROLL_ZONE_PX) * EDGE_SCROLL_MAX_SPEED_PX;
        } else if (rightOverflow > 0) {
          container.scrollLeft += (rightOverflow / EDGE_SCROLL_ZONE_PX) * EDGE_SCROLL_MAX_SPEED_PX;
        }
        // 카드 드래그 중 커서가 가만히 있어도 스크롤로 컬럼이 바뀐다 — 마지막 커서 x 로 대상 강사를 다시 잡는다
        if (container.scrollLeft !== scrolledFrom && isDraggingEventRef.current && dragThresholdRef.current) {
          const hoverTrainerId = findTrainerIdAtXRef.current?.(pointerX);
          setDragGhostPos(prev => {
            if (!prev) return prev;
            const nextTrainerId = hoverTrainerId ?? prev.originTrainerId;
            return nextTrainerId === prev.targetTrainerId ? prev : { ...prev, targetTrainerId: nextTrainerId };
          });
        }
      }
      edgeScrollFrameRef.current = requestAnimationFrame(step);
    };
    edgeScrollFrameRef.current = requestAnimationFrame(step);
  }, []);

  // 드래그 스크롤 핸들러
  const handleMouseDown = useCallback(e => {
    // 강사 헤더는 컬럼 순서 드래그 영역이라 가로 스크롤 팬에서 제외한다.
    // (네이티브 드래그 중에는 mouseup 이 오지 않아 팬 상태가 남는다)
    if (
      e.target.closest('.event-card') ||
      e.target.closest('button') ||
      e.target.closest('[data-trainer-header]')
    ) {
      return;
    }
    setIsDragging(true);
    setStartX(e.pageX - divRef.current.offsetLeft);
    setScrollLeft(divRef.current.scrollLeft);
    hasDraggedRef.current = false;
    divRef.current.style.cursor = 'grabbing';
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      divRef.current.style.cursor = 'grab';
    }
    // 이벤트 드래그 취소
    if (isDraggingEventRef.current) {
      isDraggingEventRef.current = false;
      dragThresholdRef.current = false;
      stopEdgeAutoScroll();
      setDragEvent(null);
      setDragGhostPos(null);
    }
  }, [isDragging, stopEdgeAutoScroll]);

  // 스크롤 드래그 종료. handleEventDragEnd와 함께 onMouseUp에서 호출됨.
  const handleScrollDragEnd = useCallback(() => {
    setIsDragging(false);
    if (divRef.current) {
      divRef.current.style.cursor = 'grab';
    }
  }, []);

  // 헤더 셀(data-trainer-id)의 가로 범위로 마우스 x 아래의 강사 컬럼을 찾는다. 없으면 null.
  const findTrainerIdAtX = useCallback(clientX => {
    const headers = divRef.current?.querySelectorAll('thead th[data-trainer-id]') || [];
    // 가로 스크롤하면 강사 헤더가 sticky 시간 열 아래로 들어가 가려진다 — 시간 열 위는 컬럼으로 잡지 않는다
    const timeHeader = divRef.current?.querySelector('thead th:first-child');
    if (timeHeader && clientX < timeHeader.getBoundingClientRect().right) return null;
    for (const header of headers) {
      const rect = header.getBoundingClientRect();
      if (clientX >= rect.left && clientX < rect.right) return Number(header.dataset.trainerId);
    }
    return null;
  }, []);

  useEffect(() => {
    findTrainerIdAtXRef.current = findTrainerIdAtX;
  }, [findTrainerIdAtX]);

  const handleMouseMove = useCallback(
    e => {
      // 이벤트 카드 드래그 처리
      if (isDraggingEventRef.current && dragEvent) {
        const deltaY = e.clientY - dragStartY;
        const deltaX = e.clientX - dragStartX;
        // 세로뿐 아니라 옆 강사 컬럼으로 가로로만 끌어도 드래그로 본다
        if (!dragThresholdRef.current && Math.hypot(deltaX, deltaY) > DRAG_THRESHOLD_PX) {
          dragThresholdRef.current = true;
          startEdgeAutoScroll();
        }
        dragPointerXRef.current = e.clientX;
        if (dragThresholdRef.current) {
          // 고스트 위치 계산 — 선택 단위 구간으로 스냅한다(주간 드롭과 같은 규칙).
          // 놓을 구간 = 원래 시작이 속한 구간 + 세로 이동량(선택 단위 반올림). 제자리 구간이면 원래 시각(14:10 등)을 유지하고,
          // 다른 구간이면 그 구간의 시작 시각으로 놓인다.
          const originalStartMinutes = dragEvent.startHour * MINUTES_PER_HOUR + dragEvent.startMinute;
          const originSlotMinutes = Math.floor(originalStartMinutes / snapMinutes) * snapMinutes;
          const slotDelta = Math.round((deltaY / cellHeight) * MINUTES_PER_HOUR / snapMinutes) * snapMinutes;
          const newStartMinutes = slotDelta === 0 ? originalStartMinutes : originSlotMinutes + slotDelta;
          const duration =
            dragEvent.endHour * MINUTES_PER_HOUR + dragEvent.endMinute - originalStartMinutes;
          const newEndMinutes = newStartMinutes + duration;

          const newStartHour = Math.floor(newStartMinutes / MINUTES_PER_HOUR);
          const newStartMin = newStartMinutes % MINUTES_PER_HOUR;
          const newEndHour = Math.floor(newEndMinutes / MINUTES_PER_HOUR);
          const newEndMin = newEndMinutes % MINUTES_PER_HOUR;

          // 종료가 24:00 이상이면 유효한 시각이 아니다(수업 시각은 하루 안에서만 표현된다)
          if (newStartMinutes >= 0 && newEndMinutes < DAY_END_MINUTES) {
            // 마우스 x 가 다른 강사 컬럼 위면 그 컬럼으로, 헤더 밖(채움 컬럼·시간 열)이면 원래 컬럼을 유지한다
            const hoverTrainerId = findTrainerIdAtX(e.clientX);
            const targetTrainerId = hoverTrainerId ?? dragEvent.trainerId;
            setDragGhostPos({
              targetTrainerId,
              originTrainerId: dragEvent.trainerId,
              startHour: newStartHour,
              startMinute: newStartMin,
              endHour: newEndHour,
              endMinute: newEndMin,
              timeLabel: `${newStartHour.toString().padStart(2, '0')}:${newStartMin.toString().padStart(2, '0')} - ${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`,
            });
          }
        }
        return;
      }

      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - divRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(walk) > DRAG_THRESHOLD_PX) {
        hasDraggedRef.current = true;
      }
      divRef.current.scrollLeft = scrollLeft - walk;
    },
    [
      isDragging,
      startX,
      scrollLeft,
      dragEvent,
      dragStartY,
      dragStartX,
      cellHeight,
      snapMinutes,
      findTrainerIdAtX,
      startEdgeAutoScroll,
    ],
  );

  // 머리글에서 마우스를 누른 곳이 화살표 버튼이었는지 — dragstart 의 target 은 draggable 인 th 라 누른 지점을 따로 기억한다
  const headerPressOnButtonRef = useRef(false);
  const handleHeaderMouseDown = useCallback(e => {
    headerPressOnButtonRef.current = !!e.target.closest?.('button');
  }, []);

  // 강사 컬럼 순서 드래그 (화살표 이동과 동일한 순서 변경을 드래그로도 제공)
  const handleTrainerDragStart = useCallback(
    (e, trainerId) => {
      // 화살표 버튼을 누른 채 끌면 버튼 클릭으로 남긴다(컬럼 드래그 시작 안 함)
      if (headerPressOnButtonRef.current) {
        headerPressOnButtonRef.current = false;
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      e.dataTransfer.effectAllowed = 'move';
      // Firefox 는 데이터가 없으면 드래그를 시작하지 않는다.
      e.dataTransfer.setData('text/plain', String(trainerId));

      const headerCell = e.currentTarget.closest('th');
      const previewMaxHeight = Math.max(
        COLUMN_PREVIEW_MIN_HEIGHT_PX,
        Math.round((divRef.current?.clientHeight ?? 0) * COLUMN_PREVIEW_HEIGHT_RATIO),
      );
      const preview = headerCell ? buildColumnDragImage(headerCell, previewMaxHeight) : null;
      if (preview) {
        e.dataTransfer.setDragImage(preview, headerCell.offsetWidth / 2, 30);
        // setDragImage 는 이 시점의 렌더 결과만 캡처하므로 바로 지워도 된다.
        setTimeout(() => preview.remove(), 0);
      }

      dragPointerXRef.current = e.clientX;
      setDragTrainerId(trainerId);
      startEdgeAutoScroll();
    },
    [startEdgeAutoScroll],
  );

  // 컨테이너 전역 dragover — 커서 X 를 계속 갱신해야 끝까지 밀었을 때 자동 스크롤이 이어진다.
  const handleContainerDragOver = useCallback(
    e => {
      if (dragTrainerId === null) return;
      e.preventDefault();
      dragPointerXRef.current = e.clientX;
    },
    [dragTrainerId],
  );

  const handleTrainerDragOver = useCallback(
    (e, trainerId) => {
      if (dragTrainerId === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTargetTrainerId(trainerId);
    },
    [dragTrainerId],
  );

  const handleTrainerDrop = useCallback(
    (e, trainerId) => {
      e.preventDefault();
      if (dragTrainerId !== null && dragTrainerId !== trainerId) {
        onTrainerReorder?.(dragTrainerId, trainerId);
      }
      stopEdgeAutoScroll();
      setDragTrainerId(null);
      setDropTargetTrainerId(null);
    },
    [dragTrainerId, onTrainerReorder, stopEdgeAutoScroll],
  );

  const handleTrainerDragEnd = useCallback(() => {
    stopEdgeAutoScroll();
    setDragTrainerId(null);
    setDropTargetTrainerId(null);
  }, [stopEdgeAutoScroll]);

  useEffect(() => stopEdgeAutoScroll, [stopEdgeAutoScroll]);

  // 놓았을 때 끌던 열이 들어갈 쪽. 삽입은 "놓은 열의 자리를 차지"하는 방식이라
  // 오른쪽으로 끌면 대상 뒤, 왼쪽으로 끌면 대상 앞에 들어간다. 어느 쪽인지 미리 보여준다.
  const dropEdge = (() => {
    if (dragTrainerId === null || dropTargetTrainerId === null) return null;
    const fromIndex = filteredTrainers.findIndex(t => t.trainerId === dragTrainerId);
    const toIndex = filteredTrainers.findIndex(t => t.trainerId === dropTargetTrainerId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return null;
    return fromIndex < toIndex ? 'right' : 'left';
  })();

  const isDropTargetColumn = trainerId =>
    dropTargetTrainerId === trainerId && dragTrainerId !== trainerId;

  // 이벤트 카드 드래그 시작
  const handleEventDragStart = useCallback(
    (e, event) => {
      // 수정 권한이 없으면 드래그를 시작하지 않는다(클릭은 그대로 동작)
      if (!canDragEvent) return;
      // 개인레슨·듀엣레슨·상담·기타만 드래그 가능 (그룹레슨은 참여자 다수라 제외)
      if (
        event.courseClassType !== 'PRIVATE_LESSON' &&
        event.courseClassType !== 'DUET_LESSON' &&
        event.courseClassType !== 'OT' &&
        event.courseClassType !== 'CONSULTATION' &&
        event.courseClassType !== 'ETC'
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      isDraggingEventRef.current = true;
      dragThresholdRef.current = false;
      setDragEvent(event);
      setDragStartY(e.clientY);
    setDragStartX(e.clientX);
      setDragGhostPos(null);
    },
    [canDragEvent],
  );

  // 이벤트 카드 드래그 종료
  const handleEventDragEnd = useCallback(() => {
    if (isDraggingEventRef.current && dragThresholdRef.current) {
      // mouseup 직후 같은 입력에서 click 이 발생한다. 그 뒤(타이머)에는 표식을 반드시 내려 다음 진짜 클릭을 막지 않는다.
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
    stopEdgeAutoScroll();
    if (isDraggingEventRef.current && dragEvent && dragThresholdRef.current && dragGhostPos) {
      // 시간이 변경된 경우에만 API 호출
      // 시간이나 강사 컬럼이 바뀐 경우에만 알린다
      const trainerChanged = dragGhostPos.targetTrainerId !== dragEvent.trainerId;
      if (
        trainerChanged ||
        dragGhostPos.startHour !== dragEvent.startHour ||
        dragGhostPos.startMinute !== dragEvent.startMinute
      ) {
        const targetTrainer = trainerChanged
          ? filteredTrainers.find(trainer => trainer.trainerId === dragGhostPos.targetTrainerId)
          : null;
        onDragTimeChange?.(
          dragEvent,
          dragGhostPos.startHour,
          dragGhostPos.startMinute,
          targetTrainer
            ? { trainerId: targetTrainer.trainerId, trainerName: targetTrainer.trainerName }
            : undefined,
        );
      }
    }
    isDraggingEventRef.current = false;
    dragThresholdRef.current = false;
    setDragEvent(null);
    setDragGhostPos(null);
  }, [dragEvent, dragGhostPos, onDragTimeChange, filteredTrainers, stopEdgeAutoScroll]);

  // 셀 클릭 핸들러 (드래그 후 클릭 방지)
  const handleCellClick = useCallback(
    (e, startMinutes, column, targetDate = null) => {
      e.stopPropagation();
      e.preventDefault();
      if (suppressClickRef.current) return;
      if (hasDraggedRef.current) {
        hasDraggedRef.current = false;
        return;
      }
      // 24:00 이후 칸(종료 24시 지점의 경계 행)은 일정을 시작할 수 없다
      if (startMinutes >= DAY_END_MINUTES) return;
      onCellClick(e, startMinutes, column, targetDate);
    },
    [onCellClick],
  );

  // 현재 시간 표시선. slotStartMinutes = 행 시작 시각(자정부터 분), rowMinutes = 행 길이(분).
  const renderCurrentTimeIndicator = (slotStartMinutes, rowMinutes, targetDate, isFirstColumn) => {
    const now = moment();
    const nowMinutes = now.hour() * MINUTES_PER_HOUR + now.minute();
    if (
      !targetDate.isSame(now, 'day') ||
      nowMinutes < slotStartMinutes ||
      nowMinutes >= slotStartMinutes + rowMinutes
    ) {
      return null;
    }

    const topPosition = ((nowMinutes - slotStartMinutes) / MINUTES_PER_HOUR) * cellHeight;
    return (
      <CurrentTimeIndicator topPosition={topPosition}>
        {isFirstColumn && <CurrentTimeDot />}
      </CurrentTimeIndicator>
    );
  };

  // 드래그 고스트 카드 렌더링. hourIndex = 행 시작 시각(시, 30분 칸이면 9.5 처럼 소수), slotHours = 행 길이(시)
  const renderDragGhost = (hourIndex, slotHours, trainerId) => {
    if (!dragThresholdRef.current || !dragGhostPos || !dragEvent) return null;
    if ((dragGhostPos.targetTrainerId ?? dragEvent.trainerId) !== trainerId) return null;

    const ghostStartHour = dragGhostPos.startHour;
    const ghostEndHour = dragGhostPos.endHour;
    const ghostStartMin = dragGhostPos.startMinute;
    const ghostEndMin = dragGhostPos.endMinute;

    const ghostStartIndex = ghostStartHour + ghostStartMin / 60;
    const ghostEndIndex = ghostEndHour + ghostEndMin / 60;

    if (ghostStartIndex > hourIndex + slotHours || ghostEndIndex <= hourIndex) return null;

    const duration = (dragEvent.endHour * 60 + dragEvent.endMinute) - (dragEvent.startHour * 60 + dragEvent.startMinute);
    const heightPercent = (duration / 60 / slotHours) * 100;
    const topPercent = ((ghostStartIndex - hourIndex) / slotHours) * 100;

    return (
      <DragGhostCard
        style={{
          position: 'absolute',
          top: `${topPercent}%`,
          height: `${heightPercent}%`,
          left: '2px',
          right: '2px',
          zIndex: 5,
        }}>
        <DragTimeLabel style={{ position: 'static', display: 'inline-block', margin: '4px' }}>
          {dragGhostPos.timeLabel}
        </DragTimeLabel>
      </DragGhostCard>
    );
  };

  // 카드 클릭 — 드래그 직후의 click 은 무시한다
  const handleEventCardClick = (e, ...args) => {
    if (suppressClickRef.current) {
      e.stopPropagation();
      return;
    }
    onEventClick(e, ...args);
  };

  // 일일 보기 이벤트 렌더링. hourIndex = 행 시작 시각(시), slotHours = 행 길이(시) — 퍼센트 위치가 행 높이 기준이라 행마다 넘긴다
  const renderDailyEvents = (hourIndex, slotHours, trainerId) => {
    return renderEventCards({
      events,
      hourIndex,
      slotHours,
      cardMinHeightPx,
      filterFn: event => event.trainerId === trainerId,
      periodTimes,
      currentDate,
      onEventClick: handleEventCardClick,
      onEventContextMenu,
      onEventDragStart: handleEventDragStart,
      dragEvent,
      ticketAlertCount,
      ticketAlertCountUnit,
      typeFilter,
      cellHeight,
    });
  };

  // 강사 컬럼 헤더 카운트 — 선택 유형 기준. 상담은 담당자(managerId)가 trainerId 자리에 오므로
  // 같은 컬럼에 붙고, 상태가 없는 유형이라 건수로만 센다.
  const getTrainerCounts = trainerId =>
    summarizeEvents(
      events.filter(event => event.trainerId === trainerId),
      typeFilter,
    );

  // 설정을 안 넘기면(다른 화면) 전부 표시. 숨긴 유형(그룹·OT·상담)은 events 에서 이미 빠져 0건이라 저절로 안 보인다.
  const isCountVisible = key => !visibleCounts || visibleCounts.includes(key);

  const columnCount = filteredTrainers.length;

  // 임직원이 적으면 화면 오른쪽이 비어 보인다 — 남는 폭만큼 자리 채움용 빈 컬럼을 만든다.
  // 헤더의 강사명 자리에는 '직원 등록' 버튼을 두어 임직원 등록으로 안내한다.
  const [fillerCount, setFillerCount] = useState(0);
  useEffect(() => {
    const updateFillerCount = () => {
      const containerWidth = divRef.current?.clientWidth || 0;
      const remaining = containerWidth - 60 - columnCount * 250;
      setFillerCount(remaining > 0 ? Math.ceil(remaining / 250) : 0);
    };
    updateFillerCount();
    window.addEventListener('resize', updateFillerCount);
    return () => window.removeEventListener('resize', updateFillerCount);
  }, [columnCount]);

  return (
    <CalendarContainer
      ref={divRef}
      isDragging={isDragging}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={() => {
        handleScrollDragEnd();
        handleEventDragEnd();
      }}
      onMouseMove={handleMouseMove}
      onDragOver={handleContainerDragOver}>
      {/* 일일 보기 테이블 */}
      <CalendarTable columnCount={columnCount + fillerCount}>
        <thead>
          <tr>
            <TableHeaderCell isTimeColumn />
            {filteredTrainers.map((trainer, trainerIdx) => {
              const counts = getTrainerCounts(trainer.trainerId);
              return (
                // 머리글 칸 전체(이름·카운트·여백)를 잡아 순서를 바꾼다. 화살표 버튼에서는 드래그를 시작하지 않는다
                <TableHeaderCell
                  key={trainer.trainerId}
                  data-trainer-id={trainer.trainerId}
                  data-trainer-header
                  title="드래그해서 순서를 바꿀 수 있습니다"
                  draggable
                  onMouseDown={handleHeaderMouseDown}
                  onDragStart={e => handleTrainerDragStart(e, trainer.trainerId)}
                  onDragEnd={handleTrainerDragEnd}
                  sx={{ cursor: 'grab', userSelect: 'none', '&:active': { cursor: 'grabbing' } }}
                  isDragSource={dragTrainerId === trainer.trainerId}
                  isDropTarget={isDropTargetColumn(trainer.trainerId)}
                  dropEdge={isDropTargetColumn(trainer.trainerId) ? dropEdge : null}
                  onDragOver={e => handleTrainerDragOver(e, trainer.trainerId)}
                  onDrop={e => handleTrainerDrop(e, trainer.trainerId)}>
                  <TrainerHeaderRow>
                    <TrainerArrowButton
                      type="button"
                      onClick={() => onTrainerMove?.(trainer.trainerId, 'left')}
                      disabled={trainerIdx === 0}
                      aria-label="이전 위치로">
                      <KeyboardArrowLeftIcon fontSize="small" />
                    </TrainerArrowButton>
                    <TrainerHeaderLabel>{trainer.trainerName}</TrainerHeaderLabel>
                    <TrainerArrowButton
                      type="button"
                      onClick={() => onTrainerMove?.(trainer.trainerId, 'right')}
                      disabled={trainerIdx === filteredTrainers.length - 1}
                      aria-label="다음 위치로">
                      <KeyboardArrowRightIcon fontSize="small" />
                    </TrainerArrowButton>
                  </TrainerHeaderRow>
                  <TrainerHeaderCounts>
                    {(typeFilter === TYPE_FILTER_ALL || typeFilter === TYPE_FILTER_PRIVATE || typeFilter === TYPE_FILTER_OT) && (
                      <>
                        {isCountVisible('RESERVED') && (
                          <TrainerHeaderCountItem countColor={ATTEND_STATUS_META.reserved.countColor}>
                            예약 {counts.reserved}
                          </TrainerHeaderCountItem>
                        )}
                        {isCountVisible('ATTENDED') && (
                          <TrainerHeaderCountItem countColor={ATTEND_STATUS_META.attended.countColor}>
                            출석 {counts.attended}
                          </TrainerHeaderCountItem>
                        )}
                        {isCountVisible('ABSENT') && (
                          <TrainerHeaderCountItem countColor={ATTEND_STATUS_META.absented.countColor}>
                            결석 {counts.absented}
                          </TrainerHeaderCountItem>
                        )}
                      </>
                    )}
                    {counts.groupCount > 0 && (
                      <TrainerHeaderCountItem countColor={SUMMARY_EXTRA_COUNT_META.group.color}>
                        그룹 {counts.groupCount}
                      </TrainerHeaderCountItem>
                    )}
                    {counts.otCount > 0 && (
                      <TrainerHeaderCountItem countColor={SUMMARY_EXTRA_COUNT_META.ot.color}>
                        OT {counts.otCount}
                      </TrainerHeaderCountItem>
                    )}
                    {counts.consultCount > 0 && (
                      <TrainerHeaderCountItem countColor={SUMMARY_EXTRA_COUNT_META.consult.color}>
                        상담 {counts.consultCount}
                      </TrainerHeaderCountItem>
                    )}
                  </TrainerHeaderCounts>
                </TableHeaderCell>
              );
            })}
            {Array.from({ length: fillerCount }, (_, fillerIdx) => (
              <TableHeaderCell key={`filler-head-${fillerIdx}`}>
                <FillerRegisterButton type="button" onClick={() => navigate('/employee')}>
                  + 직원 등록
                </FillerRegisterButton>
              </TableHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {hours.map(row => {
            // 행 시작 시각 — 분(자정부터)과 시(소수) 두 가지로 쓴다. 행 높이는 행 길이에 비례(마지막 짧은 행 포함)
            const slotStartMinutes = row.startMinutes;
            const slotStartHour = slotStartMinutes / MINUTES_PER_HOUR;
            const rowSlotHours = row.minutes / MINUTES_PER_HOUR;
            const rowHeight = cellHeight * rowSlotHours;
            return (
            <tr key={slotStartMinutes}>
              <TimeCell cellHeight={rowHeight}>{row.label}</TimeCell>
              {filteredTrainers.map(trainer => {
                const originalIndex = trainers.findIndex(t => t.trainerId === trainer.trainerId);
                return (
                  <EventCell
                    key={trainer.trainerId}
                    columnCount={filteredTrainers.length}
                    cellHeight={rowHeight}
                    isDraggingEvent={!!dragEvent}
                    isDragSource={dragTrainerId === trainer.trainerId}
                    isDropTarget={isDropTargetColumn(trainer.trainerId)}
                    dropEdge={isDropTargetColumn(trainer.trainerId) ? dropEdge : null}
                    onDragOver={e => handleTrainerDragOver(e, trainer.trainerId)}
                    onDrop={e => handleTrainerDrop(e, trainer.trainerId)}
                    onMouseMove={e => updateHoverBox(e, row)}
                    onClick={e => handleCellClick(e, snapStartAt(e, row), originalIndex)}
                    onContextMenu={e => {
                      // 카드 위 우클릭은 카드가 복사로 처리한다(복사 불가 유형이면 아무 메뉴도 띄우지 않는다).
                      if (e.target.closest('.event-card')) return;
                      const startMinutes = snapStartAt(e, row);
                      // 24:00 이후 칸에는 붙여넣지 않는다
                      if (startMinutes >= DAY_END_MINUTES) {
                        e.preventDefault();
                        return;
                      }
                      onCellContextMenu?.(e, startMinutes, originalIndex);
                    }}>
                    {/* 30분 보조선 — 30분 단위면 행 경계가 곧 30분 선이라 그리지 않는다 */}
                    {timeUnitMinutes === MINUTES_PER_HOUR && <RowDivider />}
                    {renderCurrentTimeIndicator(
                      slotStartMinutes,
                      row.minutes,
                      currentDate,
                      trainer.trainerId === filteredTrainers[0]?.trainerId,
                    )}
                    {renderDailyEvents(slotStartHour, rowSlotHours, trainer.trainerId)}
                    {renderDragGhost(slotStartHour, rowSlotHours, trainer.trainerId)}
                  </EventCell>
                );
              })}
              {Array.from({ length: fillerCount }, (_, fillerIdx) => (
                <EventCell
                  key={`filler-cell-${fillerIdx}`}
                  columnCount={columnCount + fillerCount}
                  cellHeight={rowHeight}>
                  {timeUnitMinutes === MINUTES_PER_HOUR && <RowDivider />}
                  {/* 현재 시간 바는 빈 컬럼까지 이어져야 한 줄로 보인다 */}
                  {renderCurrentTimeIndicator(slotStartMinutes, row.minutes, currentDate, false)}
                </EventCell>
              ))}
            </tr>
            );
          })}
        </tbody>
      </CalendarTable>
    </CalendarContainer>
  );
};

export default PrivateLessonCalendar;
